import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  AreaChart,
  Area,
} from "recharts";
import { useViewMode } from "@/components/ViewModeContext";

const RED = "#E2333F";

function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#0F172A] text-white text-xs px-3 py-2 rounded-sm shadow-lg border border-[#0F172A]">
      <div className="font-semibold mb-0.5">{label}</div>
      <div className="font-display font-bold text-sm">
        {formatter ? formatter(payload[0].value) : payload[0].value}
      </div>
    </div>
  );
}

function DataView({ data = [], height, formatter, testId }) {
  return (
    <div
      className="w-full overflow-y-auto"
      style={{ height }}
      data-testid={testId ? `${testId}-data` : "trend-data"}
    >
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
        {data.map((d, i) => {
          const last = i === data.length - 1;
          return (
            <div
              key={d.mes}
              className={`rounded-sm px-2 py-1.5 border ${
                last ? "bg-[#E2333F]/8 border-[#E2333F]/30" : "bg-slate-50 border-slate-100"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold leading-none">
                {d.mes}
              </div>
              <div
                className={`font-display font-bold text-sm tabular-nums leading-tight mt-0.5 ${
                  last ? "text-[#E2333F]" : "text-slate-900"
                }`}
              >
                {formatter ? formatter(d.valor) : d.valor}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TrendChart({ data = [], height = 200, formatter, area = true, testId }) {
  const { showData } = useViewMode();
  if (showData) {
    return <DataView data={data} height={height} formatter={formatter} testId={testId} />;
  }
  const Chart = area ? AreaChart : LineChart;
  return (
    <div className="w-full" style={{ height }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
          {area && (
            <defs>
              <linearGradient id="zaeRed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={RED} stopOpacity={0.22} />
                <stop offset="100%" stopColor={RED} stopOpacity={0} />
              </linearGradient>
            </defs>
          )}
          <CartesianGrid strokeDasharray="3 3" stroke="#E9ECF1" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: "#94A3B8" }}
            axisLine={{ stroke: "#E2E8F0" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#94A3B8" }}
            axisLine={false}
            tickLine={false}
            width={48}
            tickFormatter={(v) => (formatter ? formatter(v) : v)}
          />
          <Tooltip content={<ChartTooltip formatter={formatter} />} cursor={{ stroke: RED, strokeWidth: 1, strokeOpacity: 0.3 }} />
          {area ? (
            <Area
              type="monotone"
              dataKey="valor"
              stroke={RED}
              strokeWidth={2.5}
              fill="url(#zaeRed)"
              dot={{ r: 2.5, fill: RED, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: RED, stroke: "#fff", strokeWidth: 2 }}
            />
          ) : (
            <Line
              type="monotone"
              dataKey="valor"
              stroke={RED}
              strokeWidth={2.5}
              dot={{ r: 2.5, fill: RED, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: RED, stroke: "#fff", strokeWidth: 2 }}
            />
          )}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}
